import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Plus,
  X,
  AlertCircle,
  CheckCircle,
  TrendingDown,
  Calendar as CalendarIcon,
  Route,
  DollarSign,
  Clock
} from 'lucide-react';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { getOfficialVisaUrl } from '@/utils/officialVisaUrls';
import { getVisaOnArrivalInfo, isEligibleForVOA } from '@/utils/visaOnArrivalData';

interface CountrySelection {
  country: string;
  arrivalDate?: Date;
  departureDate?: Date;
}

interface VisaPlannerProps {
  passportCountry: string;
  onClose: () => void;
}

// Asian countries for multi-country planning
const ASIAN_COUNTRIES = [
  'Thailand', 'Vietnam', 'Cambodia', 'Laos', 'Indonesia', 'Malaysia', 'Singapore',
  'Philippines', 'Myanmar', 'Brunei', 'Japan', 'South Korea', 'China', 'Taiwan',
  'Hong Kong', 'Macau', 'India', 'Sri Lanka', 'Nepal', 'Bhutan', 'Maldives',
  'Bangladesh', 'Pakistan', 'United Arab Emirates', 'Qatar', 'Oman', 'Bahrain',
  'Kuwait', 'Jordan', 'Turkey', 'Kazakhstan', 'Uzbekistan', 'Kyrgyzstan',
  'Tajikistan', 'Turkmenistan', 'Mongolia'
].sort();

export function MultiCountryVisaPlanner({ passportCountry, onClose }: VisaPlannerProps) {
  const [selectedCountries, setSelectedCountries] = useState<CountrySelection[]>([]);
  const [currentCountry, setCurrentCountry] = useState<string>('');
  const [showOptimization, setShowOptimization] = useState(false);

  const addCountry = () => {
    if (!currentCountry) {
      toast.error('Please select a country');
      return;
    }
    
    if (selectedCountries.some(c => c.country === currentCountry)) {
      toast.error('Country already added');
      return;
    }

    setSelectedCountries([...selectedCountries, { country: currentCountry }]);
    setCurrentCountry('');
  };

  const removeCountry = (index: number) => {
    setSelectedCountries(selectedCountries.filter((_, i) => i !== index));
  };

  const updateCountryDate = (index: number, field: 'arrivalDate' | 'departureDate', date?: Date) => {
    const updated = [...selectedCountries];
    updated[index] = { ...updated[index], [field]: date };
    setSelectedCountries(updated);
  };

  // Calculate visa complexity score (lower is better)
  const getVisaComplexity = (country: string): number => {
    const voaInfo = getVisaOnArrivalInfo(country);
    if (isEligibleForVOA(country, passportCountry)) return 1; // VOA - easiest
    
    // Check for e-visa availability (medium complexity)
    const officialUrl = getOfficialVisaUrl(country);
    if (officialUrl?.includes('evisa') || officialUrl?.includes('e-visa')) return 2;
    
    // Embassy visa (most complex)
    return 3;
  };

  // Calculate estimated visa cost
  const getVisaCost = (country: string): number => {
    const voaInfo = getVisaOnArrivalInfo(country);
    if (voaInfo && voaInfo.cost) {
      // Convert to USD estimate (rough conversion)
      const { amount, currency } = voaInfo.cost;
      if (currency === 'USD') return amount;
      if (currency === 'THB') return amount / 35;
      if (currency === 'IDR') return amount / 15000;
      if (currency === 'JOD') return amount * 1.4;
      if (currency === 'BHD') return amount * 2.65;
    }
    // Rough estimates for other visa types
    return 50; // Average visa cost
  };

  // Optimize country order
  const optimizeOrder = (): CountrySelection[] => {
    if (selectedCountries.length < 2) return selectedCountries;

    const scored = selectedCountries.map(c => ({
      ...c,
      complexity: getVisaComplexity(c.country),
      cost: getVisaCost(c.country)
    }));

    // Sort by: complexity first (easiest first), then by cost (cheapest first)
    return scored.sort((a, b) => {
      if (a.complexity !== b.complexity) return a.complexity - b.complexity;
      return a.cost - b.cost;
    });
  };

  const handleOptimize = () => {
    const optimized = optimizeOrder();
    setSelectedCountries(optimized);
    setShowOptimization(true);
    toast.success('Route optimized for minimal visa complexity and cost!');
  };

  const getTotalEstimatedCost = (): number => {
    return selectedCountries.reduce((sum, c) => sum + getVisaCost(c.country), 0);
  };

  const getVisaTypeLabel = (country: string): { label: string; color: string } => {
    if (isEligibleForVOA(country, passportCountry)) {
      return { label: 'Visa on Arrival', color: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' };
    }
    const officialUrl = getOfficialVisaUrl(country);
    if (officialUrl?.includes('evisa') || officialUrl?.includes('e-visa')) {
      return { label: 'E-Visa', color: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200' };
    }
    return { label: 'Embassy Visa', color: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200' };
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold mb-2 flex items-center gap-2">
          <Route className="h-5 w-5 text-primary" />
          Multi-Country Asia Trip Planner
        </h3>
        <p className="text-sm text-muted-foreground">
          Plan your multi-country Asia trip and get optimized visa requirements
        </p>
      </div>

      {/* Add Countries */}
      <div className="space-y-3">
        <label className="text-sm font-medium">Add countries to your trip:</label>
        <div className="flex gap-2">
          <Select value={currentCountry} onValueChange={setCurrentCountry}>
            <SelectTrigger className="flex-1">
              <SelectValue placeholder="Select a country" />
            </SelectTrigger>
            <SelectContent className="max-h-60">
              {ASIAN_COUNTRIES.filter(
                c => !selectedCountries.some(sc => sc.country === c)
              ).map((country) => (
                <SelectItem key={country} value={country}>
                  {country}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button onClick={addCountry} size="icon" variant="outline">
            <Plus className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Selected Countries */}
      {selectedCountries.length > 0 && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <label className="text-sm font-medium">Your trip itinerary:</label>
            {selectedCountries.length >= 2 && (
              <Button onClick={handleOptimize} size="sm" variant="outline" className="gap-2">
                <TrendingDown className="h-4 w-4" />
                Optimize Route
              </Button>
            )}
          </div>

          <div className="space-y-2">
            {selectedCountries.map((selection, index) => {
              const visaType = getVisaTypeLabel(selection.country);
              const voaInfo = isEligibleForVOA(selection.country, passportCountry)
                ? getVisaOnArrivalInfo(selection.country)
                : null;
              const cost = getVisaCost(selection.country);

              return (
                <Card key={index} className="p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="font-semibold">{index + 1}. {selection.country}</span>
                        <Badge className={visaType.color}>{visaType.label}</Badge>
                      </div>
                      <div className="flex items-center gap-4 text-sm text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <DollarSign className="h-3 w-3" />
                          ~${cost}
                        </span>
                        {voaInfo && (
                          <span className="flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            {voaInfo.maxStayDays} days max
                          </span>
                        )}
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => removeCountry(index)}
                      className="h-8 w-8"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>

                  {/* Date Selection */}
                  <div className="grid grid-cols-2 gap-2">
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          size="sm"
                          className={cn(
                            'justify-start text-left font-normal',
                            !selection.arrivalDate && 'text-muted-foreground'
                          )}
                        >
                          <CalendarIcon className="mr-2 h-3 w-3" />
                          {selection.arrivalDate ? format(selection.arrivalDate, 'MMM dd') : 'Arrival'}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0">
                        <Calendar
                          mode="single"
                          selected={selection.arrivalDate}
                          onSelect={(date) => updateCountryDate(index, 'arrivalDate', date)}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>

                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          size="sm"
                          className={cn(
                            'justify-start text-left font-normal',
                            !selection.departureDate && 'text-muted-foreground'
                          )}
                        >
                          <CalendarIcon className="mr-2 h-3 w-3" />
                          {selection.departureDate ? format(selection.departureDate, 'MMM dd') : 'Departure'}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0">
                        <Calendar
                          mode="single"
                          selected={selection.departureDate}
                          onSelect={(date) => updateCountryDate(index, 'departureDate', date)}
                          disabled={(date) =>
                            selection.arrivalDate ? date < selection.arrivalDate : false
                          }
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                  </div>

                  {/* VOA Details */}
                  {voaInfo && (
                    <Alert className="mt-3 bg-green-50 border-green-200 dark:bg-green-950 dark:border-green-900">
                      <CheckCircle className="h-4 w-4 text-green-600 dark:text-green-400" />
                      <AlertDescription className="text-xs text-green-800 dark:text-green-200">
                        <div className="space-y-1">
                          <p><strong>Cost:</strong> {voaInfo.cost?.amount} {voaInfo.cost?.currency}</p>
                          <p><strong>Available at:</strong> {voaInfo.airports?.slice(0, 2).join(', ')}</p>
                          {voaInfo.requirements.returnTicket && (
                            <p>• Return ticket required</p>
                          )}
                          {voaInfo.requirements.hotelBooking && (
                            <p>• Hotel booking required</p>
                          )}
                        </div>
                      </AlertDescription>
                    </Alert>
                  )}
                </Card>
              );
            })}
          </div>
        </div>
      )}

      {/* Summary */}
      {selectedCountries.length > 0 && (
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            <div className="space-y-1 text-sm">
              <p><strong>Total Countries:</strong> {selectedCountries.length}</p>
              <p><strong>Estimated Visa Costs:</strong> ~${getTotalEstimatedCost()}</p>
              {showOptimization && (
                <p className="text-green-600 dark:text-green-400 flex items-center gap-1 mt-2">
                  <CheckCircle className="h-4 w-4" />
                  Route optimized for easiest visa process and lowest cost
                </p>
              )}
            </div>
          </AlertDescription>
        </Alert>
      )}

      <div className="flex gap-2">
        <Button onClick={onClose} variant="outline" className="flex-1">
          Close
        </Button>
        {selectedCountries.length > 0 && (
          <Button
            onClick={() => {
              toast.success('Trip plan saved! Check each country for detailed visa requirements.');
            }}
            className="flex-1"
          >
            Save Trip Plan
          </Button>
        )}
      </div>
    </div>
  );
}
